/**
 * Schema-driven forms.
 *
 * A form is declared as a zod schema (the *same* one the API validates against, from
 * `@ias/shared`) plus a field descriptor list. That means:
 *
 *   • client and server can never disagree about what is valid — there is one rule set
 *   • a validation message written once appears in both places, already translated
 *   • adding a field is one descriptor, not a label + input + state + error block
 *
 * Server-side field errors are mapped back onto the matching inputs, so a duplicate
 * ΑΦΜ highlights the ΑΦΜ box rather than surfacing as a generic banner.
 */

import * as React from 'react';
import {
  useForm,
  type DefaultValues,
  type FieldValues,
  type Path,
  type Resolver,
  type UseFormReturn,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ZodTypeAny } from 'zod';

import { Input, Textarea } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox, Switch } from '@/components/ui/misc';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useEnumOptions } from '@/components/common/enum-badge';
import type { EnumName } from '@/lib/registry/enums';
import { ApiRequestError } from '@/lib/api/client';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';

export interface SelectOption {
  value: string;
  label: string;
}

export type FieldDescriptor<T extends FieldValues> = {
  name: Path<T>;
  label: string;
  hint?: string;
  placeholder?: string;
  required?: boolean;
  /** Column span within the 2-column grid; `full` spans the row. */
  span?: 1 | 2;
  /** Hides the field unless the predicate passes — e.g. company-only fields. */
  visible?: (values: T) => boolean;
  disabled?: boolean;
} & (
  | { type: 'text' | 'email' | 'tel' | 'date' | 'month' | 'number' | 'money' | 'percent' | 'textarea' }
  | { type: 'enum'; enum: EnumName }
  | { type: 'select'; options: SelectOption[]; loading?: boolean }
  | { type: 'checkbox' | 'switch' }
);

interface AutoFormProps<T extends FieldValues> {
  schema: ZodTypeAny;
  defaultValues: DefaultValues<T>;
  fields: FieldDescriptor<T>[];
  onSubmit: (values: T) => Promise<void> | void;
  children?: (form: UseFormReturn<T>) => React.ReactNode;
  id?: string;
  className?: string;
}

/**
 * Validates against the shared schema, but treats an empty field as *absent* first.
 *
 * An unselected `<select>` and a cleared text input both produce `''`. Handing that to
 * a schema expecting an optional UUID or an optional email fails — `''` is neither a
 * valid value nor `undefined` — so the form silently refuses to submit while showing no
 * error on any visible field. Normalising here, in one place, is what keeps every form
 * honest; doing it at each call site is how the bug got in.
 */
function emptyStringAwareResolver<T extends FieldValues>(schema: ZodTypeAny): Resolver<T> {
  const inner = zodResolver(schema as never) as unknown as Resolver<T>;

  return async (values, context, options) => {
    // An unselected <select> and a cleared input both yield ''. A schema expecting an
    // optional UUID, date or email rejects that — '' is neither a valid value nor
    // `undefined` — and the form refuses to submit while showing an error on a control
    // the user never touched. Normalised once here rather than at every call site.
    const normalised: Record<string, unknown> = { ...values };
    for (const key of Object.keys(normalised)) {
      if (normalised[key] === '') normalised[key] = undefined;
    }

    return inner(normalised as T, context, options);
  };
}

export function AutoForm<T extends FieldValues>({
  schema,
  defaultValues,
  fields,
  onSubmit,
  children,
  id,
  className,
}: AutoFormProps<T>) {
  const form = useForm<T>({
    resolver: emptyStringAwareResolver<T>(schema),
    defaultValues,
    // Validate on blur, then live once a field has errored — nagging on every
    // keystroke of a half-typed ΑΦΜ is noise, but a corrected value should clear.
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const values = form.watch();

  const submit = form.handleSubmit(async (data) => {
    try {
      await onSubmit(data as unknown as T);
    } catch (error) {
      if (error instanceof ApiRequestError) {
        // Map server field errors onto the inputs that produced them.
        const fieldErrors = error.fieldErrors;
        let matched = false;

        for (const [field, message] of Object.entries(fieldErrors)) {
          if (fields.some((descriptor) => descriptor.name === field)) {
            form.setError(field as Path<T>, { type: 'server', message });
            matched = true;
          }
        }

        if (!matched) form.setError('root', { type: 'server', message: error.message });
      } else {
        throw error;
      }
    }
  });

  return (
    <form id={id} onSubmit={submit} className={cn('space-y-4', className)} noValidate>
      <div className="grid gap-x-4 gap-y-3.5 sm:grid-cols-2">
        {fields.map((field) => {
          if (field.visible && !field.visible(values as T)) return null;
          return (
            <FormField key={String(field.name)} field={field} form={form} />
          );
        })}
      </div>

      {form.formState.errors.root && (
        <p
          role="alert"
          className="bg-[var(--tint-critical-bg)] text-destructive border-destructive/25 rounded-md border px-3 py-2 text-[13px]"
        >
          {form.formState.errors.root.message}
        </p>
      )}

      {children?.(form)}
    </form>
  );
}

function FormField<T extends FieldValues>({
  field,
  form,
}: {
  field: FieldDescriptor<T>;
  form: UseFormReturn<T>;
}) {
  const { t } = useI18n();
  const name = field.name;
  const error = form.formState.errors[name as keyof typeof form.formState.errors] as
    | { message?: string }
    | undefined;
  const errorId = `${String(name)}-error`;

  const enumOptions = useEnumOptions(field.type === 'enum' ? field.enum : 'role');
  const options: SelectOption[] =
    field.type === 'enum' ? enumOptions : field.type === 'select' ? field.options : [];

  const spanClass = field.span === 2 || field.type === 'textarea' ? 'sm:col-span-2' : undefined;

  if (field.type === 'checkbox' || field.type === 'switch') {
    const value = Boolean(form.watch(name));
    return (
      <div className={cn('flex items-center gap-2.5 self-end pb-1', spanClass)}>
        {field.type === 'switch' ? (
          <Switch
            id={String(name)}
            checked={value}
            onCheckedChange={(checked) => form.setValue(name, checked as never, { shouldDirty: true })}
            disabled={field.disabled}
          />
        ) : (
          <Checkbox
            id={String(name)}
            checked={value}
            onCheckedChange={(checked) =>
              form.setValue(name, Boolean(checked) as never, { shouldDirty: true })
            }
            disabled={field.disabled}
          />
        )}
        <Label htmlFor={String(name)} className="cursor-pointer">
          {field.label}
        </Label>
      </div>
    );
  }

  return (
    <div className={cn('space-y-1.5', spanClass)}>
      <div className="flex items-baseline justify-between gap-2">
        <Label htmlFor={String(name)}>
          {field.label}
          {field.required && <span className="text-destructive ml-0.5">*</span>}
        </Label>
        {!field.required && (
          <span className="text-muted-foreground/70 text-[11px]">{t('common.optional')}</span>
        )}
      </div>

      {field.type === 'textarea' ? (
        <Textarea
          id={String(name)}
          rows={3}
          placeholder={field.placeholder}
          disabled={field.disabled}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          {...form.register(name)}
        />
      ) : field.type === 'enum' || field.type === 'select' ? (
        <Select
          value={(form.watch(name) as string) ?? ''}
          onValueChange={(value) => form.setValue(name, value as never, { shouldDirty: true, shouldValidate: true })}
          disabled={field.disabled}
        >
          <SelectTrigger id={String(name)} aria-invalid={Boolean(error)}>
            <SelectValue placeholder={field.placeholder ?? field.label} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <Input
          id={String(name)}
          type={inputTypeOf(field.type)}
          inputMode={field.type === 'money' || field.type === 'percent' ? 'decimal' : undefined}
          step={field.type === 'money' || field.type === 'percent' ? '0.01' : undefined}
          placeholder={field.placeholder}
          disabled={field.disabled}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          className={field.type === 'money' || field.type === 'percent' ? 'tabular' : undefined}
          {...form.register(name)}
        />
      )}

      {error?.message ? (
        <p id={errorId} role="alert" className="text-destructive text-xs">
          {localiseValidationMessage(error.message, t)}
        </p>
      ) : field.hint ? (
        <p className="text-muted-foreground text-xs">{field.hint}</p>
      ) : null}
    </div>
  );
}

function inputTypeOf(type: string): string {
  switch (type) {
    case 'money':
    case 'percent':
    case 'number':
      return 'number';
    case 'date':
      return 'date';
    case 'month':
      return 'month';
    case 'email':
      return 'email';
    case 'tel':
      return 'tel';
    default:
      return 'text';
  }
}

/**
 * Translates the validation messages Zod produces itself.
 *
 * Every rule written in `@ias/shared` carries a bilingual message, but Zod's *built-in*
 * failures — a missing required field, a wrong type — do not pass through our schemas at
 * all: they come out of the library in English. A Greek form showing "Required" under a
 * Greek label was the visible half of that. Mapping them here rather than configuring a
 * global Zod error map keeps the shared package free of UI-language concerns, and the
 * fallback returns the original string so an unmapped message is never swallowed.
 */
function localiseValidationMessage(message: string, t: (key: string) => string): string {
  const known: Record<string, string> = {
    Required: 'common.required',
    'Invalid input': 'error.invalidInput',
    'Expected string, received null': 'common.required',
    'Expected number, received nan': 'error.invalidNumber',
  };

  const key = known[message.trim()];
  return key ? t(key) : message;
}
